import { createContext, useContext, useMemo } from 'react'
import * as React from 'react'
import noop from 'lib/function/noop'
import type { HotelMapViewData, ExperienceMapViewData, TripItineraryData } from 'tripPlanner/api/types'
import type { LocationInfoData } from 'tripPlanner/components/Chat/Message'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'

export interface OfferClickSearchParams {
  checkIn?: string
  checkOut?: string
  rooms?: Array<App.Occupants>
  flexibleMonths?: string
  flexibleNights?: FLEXIBLE_DURATION_RANGE
}

interface ChatMessageActionsContextValue {
  /** Called when multi-choice selection changes (pills/cards) */
  onMultiChoiceSelectionChange: (selection: Array<string>) => void
  /** Called when an offer card is clicked (opens hotel or experience detail drawer) */
  onOfferClick: (data: { offerId: string; searchParams?: OfferClickSearchParams; offerType?: string }) => void
  /** Called when a hotel offer card is hovered (highlights map marker) */
  onOfferHover: (offerId: string | undefined) => void
  /** Called when "Map view" button is clicked on a hotel carousel */
  onHotelMapViewClick: (data: HotelMapViewData) => void
  /** Called when "Map view" button is clicked on an experience carousel */
  onExperienceMapViewClick: (data: ExperienceMapViewData) => void
  /** Called when a place tag is clicked in message markdown */
  onPlaceClick: (data: { googlePlaceId: string; placeName: string }) => void
  /** Called when a place-search link is clicked in message markdown */
  onPlaceSearchClick: (data: { searchQuery: string; placeName: string }) => void
  /** Called when location info button is clicked on a location/place card */
  onLocationInfoClick: (data: LocationInfoData) => void
  /** Called when "Schedule callback" action link is clicked */
  onScheduleCallback: () => void
  /** Sends a chat message (used by carousel "Add to trip") */
  onSendMessage: (message: string, options?: SendMessageOptions) => void
  /** Sends the post-auth follow-up once chat ownership is ready */
  onAuthenticatedFollowUp: (signal?: AbortSignal) => Promise<void> | void
  /** Current trip itinerary data (used by hotel carousel "Add to trip") */
  itineraryData: TripItineraryData | undefined
  /** Whether dev mode is active (controls raw content rendering) */
  isDevMode: boolean
}

interface ChatMessageActionsProviderProps extends ChatMessageActionsContextValue {
  children: React.ReactNode
}

const ChatMessageActionsContext = createContext<ChatMessageActionsContextValue | undefined>(undefined)

/**
 * Provides message-part interaction callbacks to all Message components.
 *
 * ⚠️ Performance: Only pass stable values (callbacks, flags) as props.
 * Every consumer (each Message) re-renders when any context value changes.
 * Fast-changing values (e.g. hoveredHotelId, scroll position) must stay
 * in component state and flow to their targets via props or refs.
 */
export function ChatMessageActionsProvider({
  children,
  onMultiChoiceSelectionChange,
  onOfferClick,
  onOfferHover,
  onHotelMapViewClick,
  onExperienceMapViewClick,
  onPlaceClick,
  onPlaceSearchClick,
  onLocationInfoClick,
  onScheduleCallback,
  onSendMessage,
  onAuthenticatedFollowUp,
  itineraryData,
  isDevMode,
}: ChatMessageActionsProviderProps) {
  const value = useMemo<ChatMessageActionsContextValue>(() => ({
    onMultiChoiceSelectionChange,
    onOfferClick,
    onOfferHover,
    onHotelMapViewClick,
    onExperienceMapViewClick,
    onPlaceClick,
    onPlaceSearchClick,
    onLocationInfoClick,
    onScheduleCallback,
    onSendMessage,
    onAuthenticatedFollowUp,
    itineraryData,
    isDevMode,
  }), [
    onMultiChoiceSelectionChange,
    onOfferClick,
    onOfferHover,
    onHotelMapViewClick,
    onExperienceMapViewClick,
    onPlaceClick,
    onPlaceSearchClick,
    onLocationInfoClick,
    onScheduleCallback,
    onSendMessage,
    onAuthenticatedFollowUp,
    itineraryData,
    isDevMode,
  ])

  return (
    <ChatMessageActionsContext.Provider value={value}>
      {children}
    </ChatMessageActionsContext.Provider>
  )
}

/**
 * Access message-part interaction callbacks and dev mode state.
 *
 * Must be used within a ChatMessageActionsProvider — throws if the
 * provider is missing so the error surfaces immediately during
 * development rather than silently falling back to noop.
 */
export function useMessageActions(): ChatMessageActionsContextValue {
  const context = useContext(ChatMessageActionsContext)
  if (!context) {
    throw new Error('useMessageActions must be used within a ChatMessageActionsProvider')
  }
  return context
}

/**
 * Default noop value for use in tests or storybook where a full
 * provider tree is not needed.
 */
export const defaultMessageActions: ChatMessageActionsContextValue = {
  onMultiChoiceSelectionChange: noop,
  onOfferClick: noop,
  onOfferHover: noop,
  onHotelMapViewClick: noop,
  onExperienceMapViewClick: noop,
  onPlaceClick: noop,
  onPlaceSearchClick: noop,
  onLocationInfoClick: noop,
  onScheduleCallback: noop,
  onSendMessage: noop,
  onAuthenticatedFollowUp: noop,
  itineraryData: undefined,
  isDevMode: false,
}
